<template>
  <div class="appt-overlay" @click.self="close">
    <div class="appt-modal">

      <!-- ── Header ── -->
      <div class="appt-header">
        <div class="appt-header-main">
          <div class="appt-avatar" v-if="hasData && !loadingData">
            {{ initials }}
          </div>
          <div class="appt-avatar appt-avatar-sk" v-else></div>
          <div class="appt-header-info">
            <div class="appt-header-name" v-if="hasData && !loadingData">
              {{ appointmentData.fname }} {{ appointmentData.lname }}
              <span class="appt-id-badge">App #{{ appointmentData.id }}</span>
              <span class="status-pill" :class="apptStatusClass">{{ apptStatusLabel }}</span>
              <span class="status-pill pill-pay" v-if="appointmentData.payment_status === 'P'">Paid</span>
              <span class="status-pill pill-unpaid" v-if="appointmentData.payment_status === 'U'">Unpaid</span>
              <span class="status-pill pill-refund" v-if="appointmentData.payment_status === 'Refund'">Refunded</span>
            </div>
            <div class="appt-header-name-sk" v-else></div>
            <div class="appt-header-sub" v-if="hasData && !loadingData">
              <span v-if="appointmentData.service_name">
                <i class="fa fa-fingerprint"></i> {{ appointmentData.service_name }}
                <em v-if="appointmentData.is_additional"> · {{ appointmentData.is_additional }}</em>
              </span>
              <span v-if="paymentTypeLabel">
                <i class="fa fa-credit-card"></i> {{ paymentTypeLabel }}
              </span>
            </div>
            <div class="appt-header-sub-sk" v-else></div>
          </div>
        </div>
        <button class="appt-close" @click="close"><i class="fa fa-times"></i></button>
      </div>

      <!-- ── Skeleton ── -->
      <div class="appt-skeleton" v-if="loadingData">
        <div class="sk-cols">
          <div class="sk-left">
            <div class="sk-card" v-for="n in 3" :key="n">
              <div class="sk-title"></div>
              <div class="sk-row" v-for="r in (n === 1 ? 3 : 4)" :key="r">
                <div class="sk-label"></div>
                <div class="sk-value" :style="{ width: (45 + (r * 15) % 40) + '%' }"></div>
              </div>
            </div>
          </div>
          <div class="sk-right">
            <div class="sk-card sk-logs">
              <div class="sk-title"></div>
              <div class="sk-log-row" v-for="r in 7" :key="r">
                <div class="sk-log-dot"></div>
                <div class="sk-log-date"></div>
                <div class="sk-log-desc" :style="{ width: (40 + (r * 11) % 35) + '%' }"></div>
              </div>
            </div>
          </div>
        </div>
        <p class="sk-hint"><i class="fa fa-spinner fa-spin"></i>
          {{ sending_email ? 'Sending email…' : 'Loading appointment details…' }}
        </p>
      </div>

      <!-- ── No data ── -->
      <div class="appt-empty" v-else-if="!hasData">
        <i class="fa fa-inbox"></i>
        <p>Appointment details not found.</p>
      </div>

      <!-- ── Body ── -->
      <div class="appt-body" v-else>
        <div class="appt-cols">

          <!-- Left: info cards -->
          <div class="appt-detail-col">

            <!-- Appointment Details -->
            <div class="detail-card">
              <div class="detail-card-title"><i class="fa fa-calendar-alt"></i> Appointment Details</div>
              <div class="detail-grid">
                <div class="detail-row">
                  <span class="dl">Date</span>
                  <span class="dv">
                    <template v-if="appointmentData.appointment_date">
                      {{ appointmentData.appointment_date | sDateFormate }}
                    </template>
                    <span class="dv-na" v-else>N/A</span>
                  </span>
                </div>
                <div class="detail-row" v-if="appointmentData.appointment_date">
                  <span class="dl">Time</span>
                  <span class="dv">{{ appointmentData.start_time | timeFormat(appointmentData.appointment_date) }}</span>
                </div>
                <div class="detail-row">
                  <span class="dl">Amount</span>
                  <span class="dv dv-amount">${{ appointmentData.total_amount | toFixedTwoDigit }}</span>
                </div>
              </div>
            </div>

            <!-- Personal Details -->
            <div class="detail-card">
              <div class="detail-card-title"><i class="fa fa-user"></i> Personal Details</div>
              <div class="detail-grid">
                <div class="detail-row">
                  <span class="dl">First Name</span>
                  <span class="dv">{{ appointmentData.fname || '—' }}</span>
                </div>
                <div class="detail-row">
                  <span class="dl">Last Name</span>
                  <span class="dv">{{ appointmentData.lname || '—' }}</span>
                </div>
                <div class="detail-row">
                  <span class="dl">Email</span>
                  <span class="dv">{{ appointmentData.email || '—' }}</span>
                </div>
                <div class="detail-row">
                  <span class="dl">Contact</span>
                  <span class="dv">{{ appointmentData.phone_no || '—' }}</span>
                </div>
              </div>
            </div>

            <!-- Attorney Request Letter -->
            <div class="detail-card" v-if="appointmentData.request_letter">
              <div class="detail-card-title"><i class="fa fa-file-text"></i> Attorney Request Letter</div>
              <div class="detail-grid">
                <div class="detail-row" v-if="appointmentData.attorney_request_email">
                  <span class="dl">Attorney Email</span>
                  <span class="dv">{{ appointmentData.attorney_request_email }}</span>
                </div>
                <div class="detail-row">
                  <span class="dl">File</span>
                  <span class="dv">{{ appointmentData.request_letter.original_filename || 'Request letter' }}</span>
                </div>
              </div>
              <div class="request-letter-actions">
                <a :href="appointmentData.request_letter.file" target="_blank" class="request-letter-btn">
                  <i class="fa fa-eye"></i> View
                </a>
                <button class="request-letter-btn" @click="downloadRequestLetter(appointmentData.request_letter)">
                  <i class="fa fa-download"></i> Download
                </button>
              </div>
            </div>

            <!-- Company Abbreviation -->
            <div class="detail-card"
              v-if="appointmentData.abbreviation_code && appointmentData.abbreviation_code !== ''">
              <div class="detail-card-title"><i class="fa fa-building"></i> Company Abbreviation</div>
              <div class="detail-grid">
                <div class="detail-row">
                  <span class="dl">Code</span>
                  <span class="dv mono">{{ appointmentData.abbreviation_code }}</span>
                </div>
                <div class="detail-row">
                  <span class="dl">Name</span>
                  <span class="dv">{{ appointmentData.company_name || '—' }}</span>
                </div>
                <div class="detail-row">
                  <span class="dl">Address</span>
                  <span class="dv">{{ appointmentData.company_address || '—' }}</span>
                </div>
              </div>
            </div>

            <!-- Company Details (CP) -->
            <div class="detail-card"
              v-if="appointmentData.location_job && appointmentData.location_job !== '' && appointmentData.payment_type === 'CP'">
              <div class="detail-card-title"><i class="fa fa-briefcase"></i> Company Details</div>
              <div class="detail-grid">
                <div class="detail-row">
                  <span class="dl">Name</span>
                  <span class="dv">{{ appointmentData.job_client_name || '—' }}</span>
                </div>
                <div class="detail-row">
                  <span class="dl">Address</span>
                  <span class="dv">{{ appointmentData.job_client_address || '—' }}</span>
                </div>
              </div>
            </div>

            <!-- QR Code -->
            <div class="detail-card" v-if="showQrCode">
              <div class="detail-card-title"><i class="fa fa-qrcode"></i> Barcode</div>
              <img :src="appointmentData.barcode_base64" class="qr-img" alt="barcode" />
            </div>

            <!-- Payment Status Control -->
            <div class="detail-card"
              v-if="(appointmentData.payment_type === 'O' || appointmentData.payment_type === 'A' || appointmentData.payment_type === 'CP') && !showQrCode && appointmentData.appt_status !== 'Cancel' && !paymentLog">
              <div class="detail-card-title"><i class="fa fa-dollar-sign"></i> Update Payment Status</div>
              <select class="pay-select"
                v-model="appointmentData.payment_status"
                @change="updatePaymentStatus(appointmentData.id, appointmentData.payment_status)">
                <option value="U">Unpaid</option>
                <option value="P">Paid</option>
              </select>
            </div>

          </div>

          <!-- Right: actions + logs -->
          <div class="appt-logs-col">

            <!-- Action buttons (hidden in paymentLog mode) -->
            <div class="action-bar" v-if="!paymentLog">
              <button class="btn-action"
                v-if="appointmentData.appt_status !== 'Cancel' && appointmentData.appointment_date != null"
                @click="reschedule">
                <i class="fa fa-calendar-alt"></i> Reschedule
              </button>
              <button class="btn-action"
                v-if="currentUser.u.is_service_provider && is_archival === false && (appointmentData.service_code === 'fdle' || appointmentData.service_code === 'fingerprintcard')"
                @click="archiveAppointment(appointmentData.id, token, appointmentData.client_id, true)">
                <i class="fa fa-archive"></i> Archive
              </button>
              <button class="btn-action"
                v-if="appointmentData.service_code === 'apostille' && appointmentData.apostille_email_sent === false"
                @click="send_email(appointmentData.id)">
                <i class="fa fa-paper-plane"></i> Resubmit
              </button>
              <button class="btn-action"
                v-if="currentUser.u.is_service_provider && is_archival === false"
                @click="changeLocation(appointmentData.id, token, appointmentData.client_id, appointmentData)">
                <i class="fa fa-map-marker-alt"></i> Change Location
              </button>
              <button class="btn-action btn-action-danger"
                v-if="appointmentData.payment_status === 'P' && is_archival === false && currentUser.u.is_service_provider && appointmentData.appt_status === 'Cancel'"
                @click="refund(appointmentData.id, token)">
                <i class="fa fa-undo"></i> Refund
              </button>
              <button class="btn-action"
                v-if="appointmentData.appt_status !== 'Cancel' && is_archival === false && appointmentData.appt_status !== 'c' && appointmentData.has_additional_rate == 1 && !appointmentData.is_drug_testing"
                @click="additionalService(appointmentData.id, token, appointmentData.service_id, appointmentData.client_id, true)">
                <i class="fa fa-plus"></i> Additional Card
              </button>
              <button class="btn-action"
                v-if="appointmentData.appt_status !== 'Cancel' && is_archival === false && appointmentData.appt_status !== 'c' && appointmentData.appointment_date != null"
                @click="additionalnewService(appointmentData.id, token, appointmentData.client_id, true)">
                <i class="fa fa-plus-circle"></i> Add Service
              </button>
            </div>

            <!-- Logs -->
            <div class="detail-card logs-card">
              <div class="detail-card-title"><i class="fa fa-list-alt"></i> Activity Logs</div>
              <div class="logs-empty" v-if="!getappointmentLog || getappointmentLog.length === 0">
                <i class="fa fa-inbox"></i> No activity recorded
              </div>
              <table class="logs-table" v-else>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date / Time</th>
                    <th>Description</th>
                    <th>Updated By</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in getappointmentLog" :key="index">
                    <td class="lt-num">{{ index + 1 }}</td>
                    <td class="lt-date">{{ datetimeFormatAmPm(item.created_at) }}</td>
                    <td class="lt-desc">{{ item.description }}</td>
                    <td class="lt-user">{{ item.username }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

          </div>
        </div>
      </div>

      <!-- ── Footer ── -->
      <div class="appt-footer">
        <button class="btn-close-modal" @click="close">
          <i class="fa fa-times"></i> Close
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import {
  SET_APPOINTMENT_FOR_RESCHEDULE,
  SET_TIME_ZONE,
  SET_SERVICE_ID,
  SET_BOOK_NOW_FROM_SP,
  SET_ADD_RATE_QTY,
  UPDATE_PAYMENT_TYPE,
  SET_RATE_TOTAL_AMOUNT,
  APPLICANTDETAIL
} from "@/core/services/store/mutations.type";
import {
  UPDATE_APPOINTMENT_PAYMENT_STATUS,
  APPOINTMENT_REFUND,
  SEND_APOSTILLE_EMAIL
} from "@/core/services/store/actions.type";

export default {
  props: {
    showModal:       { type: Boolean, default: false },
    loadingData:     { type: Boolean, default: false },
    hasData:         { type: Boolean, default: false },
    showQrCode:      { type: Boolean, default: false },
    indexNum:        { type: Number, default: -1 },
    appointmentData: { type: Object, default: () => ({}) },
    token:           { type: String, default: "" },
    paymentLog:      { type: Boolean, default: false },
    is_archival:     { type: Boolean, default: false }
  },
  data() {
    return { sending_email: false };
  },
  computed: {
    ...mapGetters(["currentUser", "getappointmentLog"]),
    initials() {
      const f = this.appointmentData.fname || "";
      const l = this.appointmentData.lname || "";
      return ((f[0] || "") + (l[0] || "")).toUpperCase() || "?";
    },
    apptStatusLabel() {
      const s = this.appointmentData.appt_status;
      if (s === "P")      return "Pending";
      if (s === "C")      return "Completed";
      if (s === "U")      return "Unseen";
      if (s === "Cancel") return "Canceled";
      return s || "";
    },
    apptStatusClass() {
      const s = this.appointmentData.appt_status;
      if (s === "P")      return "pill-pending";
      if (s === "C")      return "pill-done";
      if (s === "Cancel") return "pill-cancel";
      return "pill-info";
    },
    paymentTypeLabel() {
      const t = this.appointmentData.payment_type;
      if (t === "C")  return "Card Payment";
      if (t === "O")  return "On-site Payment";
      if (t === "A")  return "Abbreviation";
      if (t === "CP") return "Company Payment";
      return null;
    }
  },
  mounted() {
    document.body.addEventListener("keyup", e => {
      if (e.key === "Escape") this.$emit("closemodal");
    });
  },
  methods: {
    datetimeFormatAmPm(date) {
      if (!date) return "—";
      return moment(date).format("MM/DD/YYYY h:mm a");
    },
    close() { this.$emit("closemodal"); },
    downloadRequestLetter(requestLetter) {
      fetch(requestLetter.file)
        .then(res => res.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", requestLetter.original_filename || "request-letter");
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch(() => {
          this.$toastr.e("Unable to download request letter");
        });
    },
    updatePaymentStatus(appId, payment_status) {
      this.$store.dispatch(UPDATE_APPOINTMENT_PAYMENT_STATUS, { app_id: appId, payment_status })
        .then(res => {
          this.$toastr.s(res.msg);
          this.$emit("closemodal");
          this.$emit("updatepaymentstatus", this.indexNum, this.appointmentData.payment_status, this.appointmentData.is_drug_testing);
        })
        .catch(err => { this.$toastr.e(err.msg); });
    },
    additionalService(app_id, token, service_id, client_id, enable_on_site_payment) {
      this.$store.commit(SET_SERVICE_ID, service_id);
      this.$store.commit(SET_BOOK_NOW_FROM_SP, true);
      this.$store.commit(SET_ADD_RATE_QTY, 0);
      this.$store.commit(SET_RATE_TOTAL_AMOUNT, 0);
      this.$store.commit(SET_TIME_ZONE, this.appointmentData.client_timezone);
      this.$store.commit(UPDATE_PAYMENT_TYPE, "C");
      this.$router.push({ name: "client.additional.appointmnet", params: { token, app_id, id: client_id, onsite: enable_on_site_payment } });
    },
    archiveAppointment(app_id, token, client_id, enable_on_site_payment) {
      this.$store.commit(SET_TIME_ZONE, this.appointmentData.client_timezone);
      this.$store.commit(UPDATE_PAYMENT_TYPE, "C");
      this.$router.push({ name: "client.archive.appointment", params: { token, app_id, client_id, onsite: enable_on_site_payment } });
    },
    additionalnewService(app_id, token, service_id, enable_on_site_payment) {
      this.$store.commit(SET_TIME_ZONE, this.appointmentData.client_timezone);
      this.$store.commit(SET_RATE_TOTAL_AMOUNT, 0);
      this.$router.push({ name: "client.new.appointmnet.service", params: { token, app_id, id: service_id, onsite: enable_on_site_payment } });
    },
    changeLocation(app_id, token, service_id, app) {
      this.$store.commit(APPLICANTDETAIL, app);
      this.$router.push({ name: "client.change.location", params: { token, app_id, id: service_id } });
    },
    send_email(app_id) {
      this.sending_email = true;
      this.$store.dispatch(SEND_APOSTILLE_EMAIL, { app_id })
        .then(() => {
          this.sending_email = false;
          this.$toastr.s("Email Will Be Sent Shortly");
          this.$emit("reload");
          this.close();
        });
    },
    refund(app_id, token) {
      this.$swal.fire({
        title: "Are you sure?",
        text: `You want to Refund Appointment no ${app_id}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        cancelButtonText: "No"
      }).then(result => {
        if (result.isConfirmed) {
          this.$store.dispatch(APPOINTMENT_REFUND, { app_id, token })
            .then(res => {
              if (res.code === 200) {
                this.$toastr.s(res.msg);
                this.appointmentData.payment_status = "Refund";
                this.$emit("reLoadTable", this.indexNum);
              }
            })
            .catch(errs => { this.errorMsg = errs.msg; });
        }
      });
    },
    reschedule() {
      this.$store.commit(SET_APPOINTMENT_FOR_RESCHEDULE, this.appointmentData);
      this.$store.commit(SET_TIME_ZONE, this.appointmentData.client_timezone);
      this.$router.push({
        name: "client.appointment.reschedule",
        params: { token: this.token, app_id: this.appointmentData.id, client_slug: this.currentUser.c.slug, id: this.appointmentData.client_id }
      });
    }
  },
  filters: {
    toFixedTwoDigit(amount) { return parseFloat(amount).toFixed(2); },
    timeFormat(value, date) {
      if (!date) return "";
      return moment(date + " " + value).format("h:mm a");
    },
    sDateFormate(date) {
      if (!date) return "";
      return moment(String(date)).format("dddd, MMMM DD, YYYY");
    }
  }
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$dark: #0d1b3e;
$border: #e8ecf4;
$green: #276749;

/* ── Overlay ── */
.appt-overlay {
  position: fixed;
  inset: 0;
  z-index: 9100;
  background: rgba(5, 13, 36, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  font-family: $font;
}

/* ── Modal shell ── */
.appt-modal {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 32px 80px rgba(5, 13, 36, 0.28);
  width: 100%;
  max-width: 1100px;
  max-height: 94vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Header ── */
.appt-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: linear-gradient(135deg, #0d1b3e 0%, #1a2f5e 100%);
  flex-shrink: 0;
  gap: 12px;
}

.appt-header-main {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.appt-avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255,255,255,0.15);
  border: 1.5px solid rgba(255,255,255,0.25);
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: 0.5px;
}

.appt-avatar-sk {
  animation: shimmer 1.4s infinite linear;
  background: linear-gradient(90deg, rgba(255,255,255,0.08) 25%, rgba(255,255,255,0.16) 50%, rgba(255,255,255,0.08) 75%);
  background-size: 300px 100%;
}

.appt-header-info { min-width: 0; }

.appt-header-name {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
}

.appt-header-name-sk {
  height: 14px;
  width: 200px;
  border-radius: 4px;
  margin-bottom: 8px;
  animation: shimmer 1.4s infinite linear;
  background: linear-gradient(90deg, rgba(255,255,255,0.08) 25%, rgba(255,255,255,0.16) 50%, rgba(255,255,255,0.08) 75%);
  background-size: 300px 100%;
}

.appt-id-badge {
  font-size: 11px;
  font-weight: 600;
  background: rgba(255,255,255,0.14);
  border: 1px solid rgba(255,255,255,0.2);
  color: rgba(255,255,255,0.9);
  border-radius: 6px;
  padding: 2px 8px;
}

/* Status pills */
.status-pill {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3px;
  border-radius: 20px;
  padding: 2px 8px;
  text-transform: uppercase;

  &.pill-pending  { background: #fef3c7; color: #92400e; }
  &.pill-done     { background: #dcfce7; color: #166534; }
  &.pill-cancel   { background: #fee2e2; color: #991b1b; }
  &.pill-info     { background: #dbeafe; color: #1e40af; }
  &.pill-pay      { background: #dcfce7; color: #166534; }
  &.pill-unpaid   { background: #fee2e2; color: #991b1b; }
  &.pill-refund   { background: #ede9fe; color: #5b21b6; }
}

.appt-header-sub {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 5px;
  font-size: 12px;
  color: rgba(255,255,255,0.55);
  flex-wrap: wrap;

  span { display: flex; align-items: center; gap: 5px; }
  i { opacity: 0.7; font-size: 11px; }
  em { font-style: normal; opacity: 0.8; }
}

.appt-header-sub-sk {
  height: 10px;
  width: 260px;
  border-radius: 3px;
  margin-top: 8px;
  animation: shimmer 1.4s infinite linear;
  background: linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.06) 75%);
  background-size: 300px 100%;
}

.appt-close {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.18);
  background: transparent;
  color: rgba(255,255,255,0.6);
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover { background: rgba(255,255,255,0.14); color: #fff; }
}

/* ── Empty ── */
.appt-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  gap: 10px;
  color: #a0aab8;
  font-size: 13px;
  flex: 1;

  i { font-size: 28px; opacity: 0.4; }
  p { margin: 0; }
}

/* ── Body ── */
.appt-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #f5f7fb;
}

.appt-cols {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}

.appt-detail-col {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 290px;
  flex-shrink: 0;
}

.appt-logs-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ── Cards ── */
.detail-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 10px;
  padding: 12px 14px;
}

.logs-card { flex: 1; min-height: 260px; }

.detail-card-title {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #a0aab8;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;

  i { color: $blue; font-size: 11px; }
}

.detail-grid { display: flex; flex-direction: column; gap: 6px; }

.detail-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 12.5px;
}

.dl {
  font-size: 11px;
  color: #a0aab8;
  white-space: nowrap;
  min-width: 72px;
  flex-shrink: 0;
}

.dv { color: $dark; font-weight: 500; word-break: break-word; }
.dv-na { color: #b0bac9; font-style: italic; }
.dv-amount { font-weight: 700; font-size: 14px; color: #166534; }
.mono { font-family: 'Courier New', monospace; font-size: 12px; }

.request-letter-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.request-letter-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  margin: 0;
  background: #fff;
  color: $blue;
  border: 1.5px solid $blue;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  font-family: $font;
  line-height: 1;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s;

  &:hover { background: rgba(56, 154, 212, 0.08); color: $blue; text-decoration: none; }
}

/* QR */
.qr-img { width: 100%; border-radius: 6px; }

/* Payment select */
.pay-select {
  width: 100%;
  height: 34px;
  border: 1.5px solid $border;
  border-radius: 7px;
  padding: 0 10px;
  font-size: 13px;
  font-family: $font;
  color: $dark;
  background: #fff;
  outline: none;
  cursor: pointer;

  &:focus { border-color: $blue; }
}

/* ── Action bar ── */
.action-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.btn-action {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1.5px solid $border;
  background: #fff;
  color: #4a5568;
  font-size: 12px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: all 0.15s;

  i { font-size: 11px; }
  &:hover { border-color: $blue; color: $blue; background: #eef7fb; }

  &.btn-action-danger {
    border-color: #fecaca;
    color: #991b1b;

    &:hover { background: #fee2e2; border-color: #f87171; }
  }
}

/* ── Logs table ── */
.logs-empty {
  text-align: center;
  padding: 28px 12px;
  color: #b0bac9;
  font-size: 12px;

  i { display: block; margin-bottom: 6px; font-size: 20px; opacity: 0.4; }
}

.logs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  font-family: $font;

  thead tr {
    border-bottom: 2px solid #eef0f8;

    th {
      padding: 6px 10px;
      font-size: 10px;
      font-weight: 700;
      color: #a0aab8;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      white-space: nowrap;
      text-align: left;
    }
  }

  tbody tr {
    border-bottom: 1px solid #f5f7fa;
    transition: background 0.12s;

    &:last-child { border-bottom: none; }
    &:hover td { background: #f8fbff; }

    td { padding: 7px 10px; color: $dark; vertical-align: top; }
  }
}

.lt-num  { color: #a0aab8; font-size: 11px; width: 28px; }
.lt-date { color: #6b7280; white-space: nowrap; font-size: 11px; }
.lt-desc { color: #374151; }
.lt-user { color: #6b7280; font-size: 11px; white-space: nowrap; }

/* ── Footer ── */
.appt-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 12px 20px;
  border-top: 1px solid $border;
  background: #fff;
  flex-shrink: 0;
}

.btn-close-modal {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 16px;
  border-radius: 7px;
  border: none;
  background: #e8ecf4;
  color: #4a5568;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: #d8dde8; }
}

/* ── Skeleton ── */
@keyframes shimmer {
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
}

%sk {
  border-radius: 4px;
  background: linear-gradient(90deg, #eef0f6 25%, #f5f7fb 50%, #eef0f6 75%);
  background-size: 600px 100%;
  animation: shimmer 1.4s infinite linear;
}

.appt-skeleton {
  flex: 1;
  padding: 16px 20px;
  background: #f5f7fb;
  overflow: hidden;
}

.sk-hint {
  text-align: center;
  font-size: 12px;
  color: #a0aab8;
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
}

.sk-cols { display: flex; gap: 14px; }

.sk-left {
  width: 290px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sk-right { flex: 1; }

.sk-card {
  background: #fff;
  border: 1px solid #eef0f6;
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.sk-logs { min-height: 260px; }

.sk-title {
  @extend %sk;
  height: 9px;
  width: 100px;
  margin-bottom: 2px;
}

.sk-row { display: flex; align-items: center; gap: 10px; }

.sk-label {
  @extend %sk;
  height: 9px;
  width: 68px;
  flex-shrink: 0;
}

.sk-value { @extend %sk; height: 9px; }

.sk-log-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 0;
  border-bottom: 1px solid #f5f7fa;
}

.sk-log-dot {
  @extend %sk;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.sk-log-date { @extend %sk; width: 90px; height: 9px; flex-shrink: 0; }
.sk-log-desc { @extend %sk; height: 9px; flex: 1; }
</style>
